import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Volume2, ShoppingBag, Leaf, Package, ShieldCheck } from "lucide-react";
import { api, formatCHF } from "@/lib/api";
import { useSeo, breadcrumb, productJsonLd } from "@/lib/seo";
import { playDrum } from "@/lib/sound";
import { useCart } from "@/context/CartContext";
import { DrumPortrait } from "@/components/DrumPortrait";
import { MateriauxLigne } from "@/components/MateriauxOrigine";
import { Reveal } from "@/components/Reveal";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, items } = useCart();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    api.product(id)
      .then(setProduct)
      .catch(() => setError(true));
  }, [id]);
  useSeo({
    title: product ? `${product.nom} ${product.numero} — Tambour artisanal en peau de ${product.peau} · Entre les Mondes` : null,
    description: product ? `${product.intention ? `${product.intention}. ` : ""}Tambour ${product.forme.toLowerCase()}${product.diametre ? ` de ${product.diametre}` : ""}, peau de ${product.peau}, fait main à Orbe par Ludovic Isoz. ${product.disponible ? "Disponible" : "Déjà adopté"} — CHF ${product.prix}.` : "",
    image: product?.image,
    type: "product",
    jsonLd: product ? [breadcrumb([["Accueil", "/"], ["Boutique", "/boutique"], [`${product.nom} ${product.numero}`, `/boutique/tambour/${product.id}`]]), productJsonLd(product)] : null,
  });

  if (error) {
    return (
      <div className="pt-44 pb-32 text-center">
        <p className="font-serif text-3xl text-bark mb-6">Ce tambour a déjà rejoint un autre foyer.</p>
        <Link to="/boutique" data-testid="product-back-boutique" className="link-line text-terra">Retour à la boutique</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="pt-44 pb-32 max-w-7xl mx-auto px-6"><div className="h-96 rounded-2xl bg-sand-dark/60 animate-pulse" /></div>;
  }

  const inCart = items.some((i) => i.id === product.id);
  const listen = () => {
    setPlaying(true);
    playDrum(product.frequence);
    setTimeout(() => setPlaying(false), 1500);
  };

  return (
    <div className="pt-28 lg:pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Link to="/boutique" data-testid="product-back" className="inline-flex items-center gap-2 text-sm text-fumee hover:text-bark link-line mb-10">
          <ArrowLeft className="w-4 h-4" /> Tous les tambours
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start [&>*]:min-w-0">
          <Reveal>
            <div className="relative bg-earth grain spotlight rounded-3xl aspect-square flex items-center justify-center overflow-hidden lg:sticky lg:top-28" data-testid="product-portrait">
              {product.image ? (
                <img src={product.image} alt={`Tambour ${product.nom}`} className="relative z-[2] w-full h-full object-cover" />
              ) : (
                <div className="w-4/5 relative z-[2]">
                  <DrumPortrait peau={product.couleur_peau} bois={product.couleur_bois} />
                </div>
              )}
              <span className="absolute top-5 left-5 z-[2] font-mono text-[10px] uppercase tracking-[0.25em] text-sand/70">
                {product.numero} · Pièce unique
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            {product.intention && <p className="eyebrow mb-4">{product.intention}</p>}
            <h1 className="font-serif text-4xl lg:text-5xl text-bark leading-tight break-words" data-testid="product-name">{product.nom}</h1>
            <p className="mt-2 font-mono text-xs text-fumee tracking-[0.2em]">{[product.peau, product.forme, product.diametre ? `${product.diametre} cm` : null].filter(Boolean).join(" · ")}</p>
            <p className="mt-6 text-fumee leading-relaxed">{product.description}</p>

            <button onClick={listen} data-testid="product-listen"
              className={`mt-8 inline-flex items-center gap-3 px-6 py-3.5 rounded-full border text-sm transition-colors duration-300 ${
                playing ? "border-terra bg-terra/10 text-terra" : "border-wood/30 text-bark hover:border-terra hover:text-terra"
              }`}>
              <Volume2 className={`w-4 h-4 ${playing ? "pulse-soft" : ""}`} />
              {playing ? "Le tambour résonne…" : "Écouter sa vibration"}
            </button>

            <dl className="mt-10 divide-y divide-sand-dark border-y border-sand-dark text-sm">
              {[
                ["Peau", product.peau],
                ["Cadre / forme", product.forme],
                product.diametre ? ["Diamètre", `${product.diametre} cm`] : null,
                ["Note de fond", `≈ ${product.frequence} Hz`],
                product.forme === "Accessoire" ? ["Fabrication", "Artisanale, garnie de poils"] : ["Mailloche", "Mailloche artisanale disponible en boutique"],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="py-3.5 flex justify-between gap-4">
                  <dt className="font-mono text-xs uppercase tracking-[0.15em] text-fumee shrink-0">{k}</dt>
                  <dd className="text-bark text-right min-w-0 break-words">{v}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <span className="font-serif text-3xl text-terra" data-testid="product-price">{formatCHF(product.prix)}</span>
              {product.disponible ? (
                <div className="grid grid-cols-2 sm:flex gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => addItem(product)}
                    disabled={inCart}
                    data-testid="product-add-cart"
                    className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-4 rounded-full border border-earth text-earth text-sm hover:bg-earth hover:text-sand transition-colors duration-300 disabled:opacity-50"
                  >
                    <ShoppingBag className="w-4 h-4" /> {inCart ? "Dans le panier" : "Ajouter"}
                  </button>
                  <button
                    onClick={() => { addItem(product); navigate("/checkout"); }}
                    data-testid="product-buy-now"
                    className="px-4 sm:px-6 py-4 rounded-full bg-terra text-earth text-sm hover:bg-earth hover:text-sand transition-colors duration-300"
                  >
                    Adopter ce tambour
                  </button>
                </div>
              ) : (
                <span data-testid="product-sold" className="font-mono text-xs uppercase tracking-[0.25em] px-5 py-3 rounded-full bg-sand-dark text-fumee">
                  A trouvé sa maison
                </span>
              )}
            </div>
            <p className="mt-4 text-xs text-fumee">Paiement TWINT, carte ou facture — livraison offerte en Suisse.</p>
            {product.forme !== "Accessoire" && <MateriauxLigne className="mt-6" testid="materiaux-ligne-produit" />}

            <div className="mt-10 grid grid-cols-3 gap-2 sm:gap-4 text-center">
              {[
                { icon: Leaf, label: "Matériaux locaux, valorisés" },
                { icon: Package, label: "Envoi suivi & assuré" },
                { icon: ShieldCheck, label: "Retour 14 jours" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="border border-sand-dark rounded-xl p-2.5 sm:p-4 min-w-0">
                  <Icon className="w-4 h-4 text-terra mx-auto mb-2" />
                  <p className="text-[11px] text-fumee leading-tight">{label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
